import { useState, useCallback } from 'react'
import { FormikHelpers } from 'formik'
import { createContainer } from 'unstated-next'
import { useRouter } from 'next/router'

import useLocalStorage from 'lib/hooks/useLocalStorage'
import useTrackingTag from 'lib/hooks/useTrackingTag'
import * as api from 'lib/api/pointOfSale'
import { AxiosError } from 'axios'

import {
  extractNameValues,
  decodeCardType,
  extractExpirationDateValues,
  stripSpaces,
} from 'lib/utils'
import { Order } from 'types/model'
import { CreditCardFormValues } from 'components/forms/CreditCardForm/CreditCardFormProps'

import Discount from './discount'
import CatalogItem from './catalogItem'
import Upsell from './upsell'

export interface ErrorState {
  showModal: boolean
  message?: string
}

export type SubmitOrder = (
  values: CreditCardFormValues,
  actions: FormikHelpers<CreditCardFormValues>,
  path: string | undefined
) => Promise<void>

export interface OrderContainer {
  order: Order | undefined
  submitOrder: SubmitOrder
  error: ErrorState
  hideError: () => void
  initCampaignId: (campaignId: string) => void
}

const useOrder = () => {
  const router = useRouter()
  const { catalogItem } = CatalogItem.useContainer()
  const { discount } = Discount.useContainer()
  const { clearUpsellOrder, displayUpsells } = Upsell.useContainer()

  const [order, setOrder] = useLocalStorage<Order | undefined>(
    'order',
    undefined
  )
  const [error, setError] = useState<ErrorState>({ showModal: false })
  const [campaignId, setCampaignId] = useState<string>(undefined!)
  const trackingTag = useTrackingTag()
  const enableUpsells = !!(catalogItem?.upsells?.length > 0)

  const fixupValidationErrors = (errors: any) => {
    return {
      ...errors,
      fullName: `${errors.firstName} ${errors.lastName}`,
      securityCode: errors.cvv,
      expirationDate: `${errors.expirationMonth} ${errors.expirationYear}`,
    }
  }

  const submitOrder = useCallback<SubmitOrder>(
    async (values, { setSubmitting, setErrors }) => {
      const clearOrder = () => {
        setOrder(undefined)
        clearUpsellOrder()
      }

      const getOrder = (
        values: CreditCardFormValues
      ): api.SubmitCreditCardOrderRequest => {
        const { cardNumber, postalCode, email, country } = values
        const { currency } = catalogItem
        const items = [
          { sku: catalogItem.sku, stepNumber: enableUpsells ? 1 : 0 },
        ]

        return {
          storefront: window.location.origin,
          campaign_id: campaignId,
          enableUpsells,
          currency,
          items,
          discountCode: discount?.code,
          ...extractNameValues(values.fullName),
          email,
          cardType: decodeCardType(cardNumber),
          cardNumber: stripSpaces(cardNumber),
          ...extractExpirationDateValues(values.expirationDate),
          cvv: values.securityCode,
          postalCode: stripSpaces(postalCode),
          country,
          ...trackingTag,
        }
      }

      try {
        // Clear order from local storage
        clearOrder()

        // Submit order to api
        const order = getOrder(values)
        const res = await api.submitOrder(order)

        // Handle server error
        if (res.status !== 200) {
          showError(res.statusText)
          return
        }

        // Handle response error
        const response = res.data
        if (!response.approved) {
          showError(response.errorMessage)
          return
        }

        // Save approved order
        setOrder({
          customerEmail: values.email,
          items: response.order.items,
          orderId: response.order.orderId,
          orderTotal: response.order.orderTotal,
          salesTaxAmount: response.order.salesTaxAmount,
          salesTaxPercent: response.order.salesTaxPercent,
          currency: response.order.currency,
        })

        // Check for upsells
        if (enableUpsells && response.upsellToken) {
          const referenceOrder = {
            ...order,
            referenceToken: response.upsellToken,
          }

          displayUpsells(referenceOrder, catalogItem.upsells)
        } else {
          await router.push('/complete')
        }
      } catch (err: unknown) {
        if (err instanceof AxiosError) {
          let { errors } = err.response?.data
          setErrors(fixupValidationErrors(errors))
        } else if (err instanceof Error) {
          showError(err.message)
        } else {
          showError(err as string)
        }

        setSubmitting(false)
      }
    },
    [
      catalogItem,
      discount,
      trackingTag,
      displayUpsells,
      clearUpsellOrder,
      enableUpsells,
      setOrder,
      campaignId,
      router,
    ]
  )

  const showError = (message: string) => {
    setError({ showModal: true, message: message })
  }

  const hideError = useCallback(() => {
    setError({ showModal: false })
  }, [])

  const initCampaignId = useCallback((campaignId: string) => {
    setCampaignId(campaignId)
  }, [])

  return { order, submitOrder, error, hideError, initCampaignId }
}

export default createContainer<OrderContainer>(useOrder)
